import { FC } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import { NotificationHooks, useFormat, useLocale, useSearchParams } from '../../../hooks/index';
import {
  notificationRequestTitle,
  NotificationType,
  requestNotificationBuilder,
} from '../../../types/notification.types';
import { SharePlanType } from '../../../types/pool-plans.types';
import { StakeholderType } from '../../../types/stakeholder.types';
import { toNumber } from '../../../utils/to-number.util';
import { AppFormattedMessage } from '../../app-formatted-message/index';
import { EmptyState, EmptyStateImageVariant, EmptyStateVariant } from '../../empty-state/index';
import Loader from '../../loader/index';
import { ApproveNotification } from '../notification/approve-notification';
import { RequestNotification } from '../notification/index';

export type RequestedTabProps = {
  companyId: string;
  currencySymbol: string;
  handleCloseModal: () => void;
  isAllRead: boolean;
  onExerciseRequest: (id: string, type: SharePlanType | undefined, isTerminated?: boolean) => void;
  setMilestoneIndex: (index: null | number) => void;
  unreadOnly: boolean;
};

export const RequestedTab: FC<RequestedTabProps> = ({
  companyId,
  onExerciseRequest,
  unreadOnly,
  isAllRead,
  setMilestoneIndex,
  currencySymbol,
  handleCloseModal,
}) => {
  const { messagesLocale } = useLocale();
  const { format } = useFormat();
  const { notifications, fetchNextPage, hasNextPage, invalidateQuery } =
    NotificationHooks.useNotifications({
      companyId,
      type: NotificationType.REQUEST,
      unreadOnly,
      resolved: false,
    });

  const { setValue } = useSearchParams('hurdleApproveId');
  const { markAsRead } = NotificationHooks.useMarkRead({ companyId, onSuccess: invalidateQuery });
  const { markAsResolved } = NotificationHooks.useMarkResolved({
    companyId,
    onSuccess: invalidateQuery,
  });

  return notifications.length ? (
    <InfiniteScroll
      className="flex w-full flex-col gap-2"
      dataLength={notifications.length || 0}
      hasMore={hasNextPage}
      loader={<Loader />}
      next={fetchNextPage}
      scrollableTarget="scroll-notifications-target"
    >
      {notifications.map(
        ({
          createdAt,
          is_read: isRead,
          id,
          company,
          stakeholderName = '',
          adminId,
          stakeholderType = StakeholderType.OTHER_INST,
          adminName = '',
          type,
          targetBasedMilestoneRequest,
          requestId = '',
          requestSharesCount = 0,
          requestPlan = '',
          numberOfGrants = 0,
          requestTotalSharesCount = 0,
          requestNextTriggerTime = '',
          requestHurdleValue,
          requestPlanType,
          resolved,
          approveRequestId,
          milestones,
          stakeholderIsTerminated,
        }) =>
          targetBasedMilestoneRequest ? (
            <ApproveNotification
              createdAt={new Date(createdAt)}
              handleCloseModal={handleCloseModal}
              isRead={isAllRead || isRead}
              key={id}
              milestones={milestones || []}
              onMarkRead={() => markAsRead({ id, type })}
              resolved={resolved}
              setMilestoneIndex={setMilestoneIndex}
              targetBasedMilestoneRequest={targetBasedMilestoneRequest}
              type={<AppFormattedMessage id="MILESTONE_APPROVED_NOTIFICATION" />}
            />
          ) : (
            <RequestNotification
              createdAt={new Date(createdAt)}
              isRead={requestPlanType === SharePlanType.GROWTH_HURDLE ? true : isAllRead || isRead}
              isTerminated={stakeholderIsTerminated}
              key={id}
              message={requestNotificationBuilder({
                hurdleValue: toNumber(requestHurdleValue),
                milestoneDate: format(requestNextTriggerTime, 'dd/MM/yyyy'),
                milestoneName: '',
                planAvailableCount: toNumber(requestTotalSharesCount),
                planGrunts: toNumber(numberOfGrants),
                planName: requestPlan,
                planType: requestPlanType || SharePlanType.BSPCE,
                requestedSharesCount: toNumber(requestSharesCount),
                adminName,
                stakeholderName,
                isAdminYou: adminId === company?.stakeholder?.id,
                stakeholderType,
                currencySymbol,
                stakeholderIsTerminated,
              })}
              onExercise={() => {
                requestPlanType === SharePlanType.GROWTH_HURDLE
                  ? setValue(approveRequestId)
                  : onExerciseRequest(requestId, requestPlanType, stakeholderIsTerminated);
              }}
              onMarkRead={() => markAsRead({ id, type })}
              onMarkResolved={() => markAsResolved(id)}
              planType={requestPlanType || SharePlanType.BSPCE}
              resolved={resolved}
              type={
                (requestPlanType && messagesLocale[notificationRequestTitle[requestPlanType]]) || ''
              }
            />
          ),
      )}
    </InfiniteScroll>
  ) : (
    <EmptyState
      colorScheme={EmptyStateVariant.EMPTY}
      description={messagesLocale['REQUESTS_WILL_BE_HERE']}
      imageVariant={unreadOnly ? EmptyStateImageVariant.CONFETTI : EmptyStateImageVariant.BELL}
      text={unreadOnly ? messagesLocale['ALL_CAUGHT_UP'] : messagesLocale['NO_REQUESTS']}
    />
  );
};
